import { useTranslation } from 'react-i18next';
import {
	Typography,
	Card,
	CardContent,
	Box,
	IconButton,
	Chip,
	Tabs,
	Tab,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
} from '@mui/material';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import {
	Link,
	useLocation,
	useParams,
	useSearchParams,
} from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import qs from 'qs';
import { filterObjectByKeys, parseSummarySearchParams } from '@/utils/qsHelper';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RemoveIcon from '@mui/icons-material/Remove';
import {
	ChangeHistoryFilter,
	ChangeHistoryDTO,
} from '@/api/DTO/member-management/admissionRequest';
import {
	useGetChangeHistoryListDetailMutation,
	useGetChangeHistoryListQuery,
} from '@/api/member-management/admissionRequest';
import LocalDataGrid from '@/common/components/dataGrid/LocalDataGrid';
import { useTabParam } from '@/hooks/useTabParam';
import { DISPLAY_DATE_TIME_FORMAT } from '@/utils/dateHelper';
import dayjs from 'dayjs';

export default function HistorySummaryPage() {
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();
	const [isInitializedForm, setIsInitializedForm] = useState<boolean>(false);
	const [currentTab, setCurrentTab] = useTabParam('0', 'historyTab');
	const [showDetail, setShowDetail] = useState(false);
	const [detailResult, setDetailResult] = useState<ChangeHistoryDTO[]>([]);
	const { admissionRequestId } = useParams();
	const location = useLocation();
	const [getChangeHistoryListDetail] = useGetChangeHistoryListDetailMutation();

	const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
		setCurrentTab(newValue);
	};

	const defaultPropertyFilter: ChangeHistoryFilter = {
		pagination: initialPagination,
		searchString: '',
		sortings: [],
		category: currentTab,
	};
	const defaultValues: DefaultValues<ChangeHistoryFilter> =
		defaultPropertyFilter;
	const methods = useForm<ChangeHistoryFilter>({
		defaultValues,
	});
	const transformedSearchParams: ChangeHistoryFilter = {
		...defaultPropertyFilter,
		...filterObjectByKeys(parseSummarySearchParams(searchParams), [
			'pagination',
			'sortings',
			'searchString',
			'category',
		]),
	};
	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetChangeHistoryListQuery(
			{ ...transformedSearchParams, id: admissionRequestId || '' },
			{
				refetchOnMountOrArgChange: true,
			}
		);

	const transformResponse = useCallback((response?: ChangeHistoryDTO[]) => {
		if (response == null) return;
		const newResponse: any[] = [];
		response.forEach((item) => {
			newResponse.push({
				...item,
				createdAt: !item.createdAt
					? '-'
					: dayjs(item.createdAt).locale('en').format(DISPLAY_DATE_TIME_FORMAT),
			});
		});
		return newResponse;
	}, []);

	// watch serach params
	useEffect(() => {
		if (!isInitializedForm) {
			methods.reset(transformedSearchParams, {
				keepDefaultValues: true,
			});
			setIsInitializedForm(true);
		}
	}, [searchParams]);

	// watch fields change
	useEffect(() => {
		const subscription = methods.watch((value) => {
			const propertyFilterFormResult = value as ChangeHistoryFilter;

			const cleanedFilter = Object.fromEntries(
				Object.entries(propertyFilterFormResult).filter(([key, val]) => {
					return val !== '' && val != null;
				})
			);

			// append to url to reload page
			setSearchParams(qs.stringify(cleanedFilter));
		});
		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const withFallback = (cols: GridColDef[]): GridColDef[] =>
		cols.map((col) => ({
			...col,
			valueFormatter:
				col.valueFormatter ??
				((value: any) => (value == null || value === '' ? '-' : value)),
		}));

	const columns: GridColDef[] = [
		...(currentTab === '0'
			? [
					{
						field: 'createdAt',
						headerName: t('member.dateTime'),
						flex: 15,
					},
					{
						field: 'newValueDesc',
						headerName: t('member.newStatus'),
						flex: 15,
					},
					{
						field: 'previousValueDesc',
						headerName: t('member.previousStatus'),
						flex: 15,
					},
					{
						field: 'actionTypeDesc',
						headerName: t('member.action'),
						flex: 15,
					},
					{
						field: 'reasonForReturn',
						headerName: t('member.details'),
						flex: 15,
					},

					{
						field: 'createdByName',
						headerName: t('member.actionedBy'),
						flex: 15,
					},
				]
			: []),
		...(currentTab === '1' || currentTab === '3' || currentTab === '4'
			? [
					{
						field: 'createdAt',
						headerName: t('member.dateTime'),
						flex: 15,
					},
					{
						field: 'fieldTypeDesc',
						headerName: t('member.field'),
						flex: 15,
					},
					{
						field: 'newValueDesc',
						headerName: t('member.newValue'),
						flex: 15,
					},
					{
						field: 'previousValueDesc',
						headerName: t('member.previousValue'),
						flex: 15,
					},
					{
						field: 'actionTypeDesc',
						headerName: t('member.action'),
						flex: 15,
					},

					{
						field: 'createdByName',
						headerName: t('member.actionedBy'),
						flex: 15,
					},
				]
			: []),
		...(currentTab === '2'
			? [
					{
						field: 'createdAt',
						headerName: t('member.dateTime'),
						flex: 15,
					},
					{
						field: 'newValueDesc',
						headerName: t('member.toState'),
						flex: 15,
					},
					{
						field: 'previousValueDesc',
						headerName: t('member.fromState'),
						flex: 15,
					},
					{
						field: 'actionTypeDesc',
						headerName: t('member.action'),
						flex: 15,
					},

					{
						field: 'createdByName',
						headerName: t('member.actionedBy'),
						flex: 15,
					},
					{
						field: 'edit',
						headerName: '',
						width: 68,
						renderCell: (params: any) => (
							<IconButton
								color='secondary'
								onClick={() => handleDetail(params.row.groupId)}
							>
								<VisibilityOutlinedIcon />
							</IconButton>
						),
					},
				]
			: []),
		...(currentTab === '5'
			? [
					{
						field: 'createdAt',
						headerName: t('member.dateTime'),
						flex: 15,
					},
					{
						field: 'newValueDesc',
						headerName: t('member.currentRiskLevel'),
						flex: 15,
						renderCell: (params: any) => {
							const { newValue, previousValue, newValueDesc } = params.row;
							const desc =
								newValueDesc == null || newValueDesc === ''
									? '-'
									: newValueDesc;
							// only show trend icon when a previous risk level exists
							const hasPrevious = previousValue != null && previousValue !== '';
							let trendIcon = null;
							if (hasPrevious && newValue != null && newValue !== '') {
								const current = Number(newValue);
								const previous = Number(previousValue);
								if (current > previous) {
									trendIcon = (
										<ArrowUpwardIcon
											fontSize='small'
											sx={{ color: '#FF5B4D' }}
										/>
									);
								} else if (current < previous) {
									trendIcon = (
										<ArrowDownwardIcon
											fontSize='small'
											sx={{ color: '#4CB848' }}
										/>
									);
								} else {
									trendIcon = (
										<RemoveIcon fontSize='small' sx={{ color: '#535862' }} />
									);
								}
							}
							return (
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
									{desc}
									{trendIcon}
								</Box>
							);
						},
					},
					{
						field: 'previousValueDesc',
						headerName: t('member.previousRiskLevel'),
						flex: 15,
					},
					{
						field: 'natureDesc',
						headerName: t('member.nature'),
						flex: 15,
					},
					{
						field: 'interventionWorkerName',
						headerName: t('member.interventionWorker'),
						flex: 15,
					},
					{
						field: 'navigation',
						headerName: '',
						width: 68,
						renderCell: (params: any) => (
							<Link
								to={`${location.pathname}/intervention/detail/${params.row.interventionId}`}
							>
								<IconButton color='secondary'>
									<EventOutlinedIcon />
								</IconButton>
							</Link>
						),
					},
				]
			: []),
	];

	const handleDetail = async (id: string) => {
		const response = await getChangeHistoryListDetail(id);
		if ('data' in response) {
			setDetailResult(response?.data?.data || []);
			setShowDetail(true);
		}
	};

	const columns2: GridColDef[] = [
		{
			field: 'fieldTypeDesc',
			headerName: t('member.field'),
			flex: 15,
		},
		{
			field: 'newValueDesc',
			headerName: t('member.to'),
			flex: 15,
		},
		{
			field: 'previousValueDesc',
			headerName: t('member.from'),
			flex: 15,
		},
	];

	return (
		<>
			<Card sx={{ marginTop: 3 }}>
				<CardContent>
					{' '}
					<Tabs
						value={currentTab}
						onChange={handleTabChange}
						sx={{ marginBottom: 3 }}
					>
						<Tab
							label={
								<Typography variant='body1'>{t('member.status')}</Typography>
							}
							value='0'
							sx={{ textTransform: 'none' }}
						/>
						<Tab
							label={
								<Typography variant='body1'>{t('member.name')}</Typography>
							}
							value='1'
							sx={{ textTransform: 'none' }}
						/>
						<Tab
							label={
								<Typography variant='body1'>
									{t('member.membership')}
								</Typography>
							}
							value='2'
							sx={{ textTransform: 'none' }}
						/>
						<Tab
							label={
								<Typography variant='body1'>
									{t('member.assignedWorker')}
								</Typography>
							}
							value='3'
							sx={{ textTransform: 'none' }}
						/>
						<Tab
							label={
								<Typography variant='body1'>{t('member.diagnosis')}</Typography>
							}
							value='4'
							sx={{ textTransform: 'none' }}
						/>
						<Tab
							label={
								<Typography variant='body1'>{t('member.riskLevel')}</Typography>
							}
							value='5'
							sx={{ textTransform: 'none' }}
						/>
					</Tabs>
					<FormProvider {...methods}>
						<Box marginTop={2}>
							<AppDataGrid
								loading={!isGetPropertySummarySuccess}
								rows={transformResponse(propertySummary?.data?.items) ?? []}
								rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
								columns={withFallback(columns)}
								getRowId={(row) => row.id}
							/>
						</Box>
					</FormProvider>
				</CardContent>
			</Card>

			<Dialog
				open={showDetail}
				onClose={() => {
					setShowDetail(false);
				}}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'
				maxWidth='lg'
				fullWidth
			>
				<DialogTitle variant='h5'>{t('member.changeDetails')}</DialogTitle>
				<DialogContent>
					<LocalDataGrid
						rows={detailResult || []}
						columns={withFallback(columns2)}
						getRowId={(row) => row.id}
						pagination={false}
					/>
				</DialogContent>
				<DialogActions>
					<Button
						variant='contained'
						onClick={() => {
							setShowDetail(false);
						}}
					>
						{t('button.close')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}
